import { useEffect, useState, useCallback } from "react";

export type UtilityChoice = "PG&E" | "SCE" | "SDG&E" | "Other";

const STORAGE_KEY = "ceag_utility_choice";

/**
 * Persists the visitor's California utility choice across the site so the
 * hero, calculator, and any localized copy can speak directly to their
 * situation. SolarShark and other national tools treat CA as generic —
 * this is our 2-second "we know California" signal.
 */
export const useUtilityPreference = () => {
  const [utility, setUtilityState] = useState<UtilityChoice | null>(null);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored === "PG&E" || stored === "SCE" || stored === "SDG&E" || stored === "Other") {
        setUtilityState(stored);
      }
    } catch {
      /* ignore */
    }
  }, []);

  const setUtility = useCallback((u: UtilityChoice) => {
    setUtilityState(u);
    try {
      localStorage.setItem(STORAGE_KEY, u);
      window.dispatchEvent(new CustomEvent("ceag:utility-changed", { detail: u }));
    } catch {
      /* ignore */
    }
  }, []);

  // Cross-component sync
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as UtilityChoice;
      setUtilityState(detail);
    };
    window.addEventListener("ceag:utility-changed", handler);
    return () => window.removeEventListener("ceag:utility-changed", handler);
  }, []);

  return { utility, setUtility };
};

/** Rate-hike subhead, editable per quarter. Swap the strings to update sitewide. */
export const getRateHikeSubhead = (utility: UtilityChoice | null): string => {
  switch (utility) {
    case "PG&E":
      return "PG&E rates climbed again this year. Here's what California homeowners are doing instead.";
    case "SCE":
      return "SCE keeps hiking rates. Here's what California homeowners are doing instead.";
    case "SDG&E":
      return "SDG&E just hiked rates again. Here's what California homeowners are doing instead.";
    default:
      return "California utilities keep raising rates. Here's what homeowners are doing instead — with $Zero down.";
  }
};
